/**
 * Microsoft Entra External ID (CIAM) Configuration
 * 
 * This file contains the MSAL configuration for authenticating users
 * via Entra External ID using Authorization Code Flow with PKCE.
 */

import { LogLevel } from "@azure/msal-browser";

// ============================================================================
// CONFIGURATION VALUES - UPDATE THESE WITH YOUR TENANT DETAILS
// ============================================================================

// Your Entra External ID tenant details
export const TENANT_ID = process.env.REACT_APP_ENTRA_TENANT_ID || "";
export const TENANT_SUBDOMAIN = process.env.REACT_APP_ENTRA_TENANT_SUBDOMAIN || "";

// User flow name (sign-up/sign-in policy)
export const USER_FLOW = process.env.REACT_APP_ENTRA_USER_FLOW || "finlytics_susi";

// Optional: Separate sign-up flow (if configured in Azure)
export const SIGNUP_USER_FLOW = process.env.REACT_APP_ENTRA_SIGNUP_USER_FLOW || USER_FLOW;

// Application (client) IDs
export const SPA_CLIENT_ID = process.env.REACT_APP_ENTRA_SPA_CLIENT_ID || "";
export const API_CLIENT_ID = process.env.REACT_APP_ENTRA_API_CLIENT_ID || "";

/**
 * Check if Entra External ID is properly configured
 */
export const isEntraConfigured = () => {
    return TENANT_ID &&
        TENANT_SUBDOMAIN &&
        SPA_CLIENT_ID &&
        API_CLIENT_ID &&
        !TENANT_ID.includes('YOUR_') &&
        !TENANT_SUBDOMAIN.includes('YOUR_') &&
        !SPA_CLIENT_ID.includes('YOUR_') &&
        !API_CLIENT_ID.includes('YOUR_');
};

// API scope for backend access
export const API_SCOPE = `api://${API_CLIENT_ID}/access_as_user`;

// Frontend URLs
export const FRONTEND_ORIGIN = process.env.REACT_APP_FRONTEND_ORIGIN || "http://localhost:3000";
export const REDIRECT_PATH = process.env.REACT_APP_REDIRECT_PATH || "/";

// ============================================================================
// AUTHORITY CONFIGURATION
// ============================================================================

/**
 * Authority format flag:
 * - 'v2': Use standard v2 authority (no user flow)
 */
const AUTHORITY_FORMAT = process.env.REACT_APP_AUTHORITY_FORMAT || 'v2';

/**
 * Construct the authority URL based on format preference
 * 
 * Format options:
 * 1. Standard v2: https://{TENANT_SUBDOMAIN}.ciamlogin.com/{TENANT_ID}/v2.0
 */
export const getAuthority = (userFlow = USER_FLOW) => {
    const baseAuthority = `https://${TENANT_SUBDOMAIN}.ciamlogin.com/${TENANT_ID}`;
    return `${baseAuthority}/v2.0`;
};

export const AUTHORITY = getAuthority();
export const SIGNUP_AUTHORITY = getAuthority(SIGNUP_USER_FLOW);

// ============================================================================
// MSAL CONFIGURATION
// ============================================================================

/**
 * Configuration for MSAL PublicClientApplication
 * Uses Authorization Code Flow with PKCE (default, secure)
 */
export const msalConfig = {
    auth: {
        clientId: SPA_CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: `${FRONTEND_ORIGIN}${REDIRECT_PATH}`,
        postLogoutRedirectUri: FRONTEND_ORIGIN, // Returns to home after logout
        navigateToLoginRequestUrl: true,
    },
    cache: {
        cacheLocation: "sessionStorage", // Options: "sessionStorage" or "localStorage"
        storeAuthStateInCookie: false, // Set to true if you need to support IE11
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return; // Don't log personally identifiable information
                }

                switch (level) {
                    case LogLevel.Error:
                        console.error(`[MSAL Error] ${message}`);
                        return;
                    case LogLevel.Info:
                        console.info(`[MSAL Info] ${message}`);
                        return;
                    case LogLevel.Verbose:
                        console.debug(`[MSAL Verbose] ${message}`);
                        return;
                    case LogLevel.Warning:
                        console.warn(`[MSAL Warning] ${message}`);
                        return;
                    default:
                        return;
                }
            },
            logLevel: LogLevel.Warning, // Adjust as needed: Error, Warning, Info, Verbose
            piiLoggingEnabled: false,
        },
    },
};

// ============================================================================
// TOKEN REQUEST SCOPES
// ============================================================================

/**
 * Scopes to request during login
 * Includes OpenID Connect scopes + API access scope
 */
export const loginRequest = {
    scopes: [
        "openid",
        "profile",
        "offline_access",
        API_SCOPE, // Backend API access
    ],
};

/**
 * Sign-up request configuration
 * Uses separate sign-up flow if configured, otherwise falls back to SUSI
 */
export const signupRequest = {
    ...loginRequest,
};

/**
 * Scopes to request when acquiring tokens for API calls
 * This should match the scope exposed by your backend API
 */
export const apiRequest = {
    scopes: [API_SCOPE],
};

/**
 * Microsoft Graph scopes (optional, for future use)
 * Currently empty as we're not using Graph API
 */
export const graphScopes = {
    scopes: [],
};

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Check if MSAL configuration is complete
 * Useful for displaying setup warnings in dev mode
 */
export const isConfigured = () => {
    return (
        TENANT_ID !== "YOUR_TENANT_ID_GUID" &&
        TENANT_SUBDOMAIN !== "YOUR_TENANT_SUBDOMAIN" &&
        SPA_CLIENT_ID !== "YOUR_FINLYTICS_SPA_CLIENT_ID_GUID" &&
        API_CLIENT_ID !== "YOUR_FINLYTICS_API_CLIENT_ID_GUID"
    );
};

/**
 * Log configuration status on module load (dev mode only)
 */
if (process.env.NODE_ENV === 'development') {
    console.log('[Auth Config] MSAL Configuration:', {
        configured: isConfigured(),
        authority: AUTHORITY,
        signupAuthority: SIGNUP_AUTHORITY,
        authorityFormat: AUTHORITY_FORMAT,
        clientId: SPA_CLIENT_ID,
        apiScope: API_SCOPE,
    });
}
